<template>
  <div class="ov-rev">
    <div class="ov-rev__chart" aria-hidden="true">
      <div class="ov-rev__yaxis" :style="yAxisStyle">
        <span
          v-for="level in levels"
          :key="'y' + level.value"
          class="ov-rev__ylabel"
          :style="{ top: level.top }"
        >{{ level.text }}</span>
      </div>
      <div class="ov-rev__main">
        <div class="ov-rev__plot" :style="plotStyle" @mouseleave="hoverIndex = null">
          <span
            v-for="level in levels"
            :key="'g' + level.value"
            class="ov-rev__grid"
            :class="{ 'ov-rev__grid--zero': level.value === 0 }"
            :style="{ top: level.lineTop }"
          ></span>
          <div class="ov-rev__bars" :style="barsStyle">
            <span
              v-for="(bar, i) in bars"
              :key="bar.date"
              class="ov-rev__col"
              :class="{ 'ov-rev__col--active': i === activeIndex }"
              @mouseenter="onEnter(i)"
              @click="onTap(i)"
            >
              <span class="ov-rev__bar" :class="{ 'ov-rev__bar--today': bar.today }" :style="{ height: bar.height }"></span>
            </span>
          </div>
          <div v-if="tip" class="ov-rev__tip" :style="tip.style">{{ tip.text }}</div>
        </div>
        <div class="ov-rev__xaxis">
          <span
            v-for="label in xLabels"
            :key="'x' + label.index"
            class="ov-rev__xlabel"
            :style="label.style"
          >{{ label.text }}</span>
        </div>
      </div>
    </div>
    <p class="ov-rev__sr app-sr-only">{{ srText }}</p>
  </div>
</template>

<script>
// Säulendiagramm „Einnahmen“ je Tag (Spezifikation 4.1.5, gen_r3.py:557–586) aus CSS, ohne DevExtreme-Chart.
// y-Achse mit vier Stufen 0 bis 3 · Schritt, Säulen --d-cmp, heute --d-foc (aktueller, vorläufiger Wert), Hover --d-std.
// Tooltip „Do, 08.10.: 1.184,60 €“ bei Hover (Maus) bzw. Antippen (Touch); tooltips=false auf dem Handy (die ganze
// Karte ist dort ein Link). Die Grafik ist aria-hidden, ein versteckter Satz nennt Zeitraum und Summe.
import moment from 'moment';
import { axisStep } from './overviewData';
import { formatDate, formatEuro, formatNumber, NBSP } from '@/utils/format';

function decimalsOf(values) {
  for (let d = 0; d < 2; d += 1) {
    const f = Math.pow(10, d);
    if (values.every(v => Math.abs(Math.round(v * f) - v * f) < 1e-6)) {
      return d;
    }
  }
  return 2;
}

export default {
  name: 'OvRevenueChart',
  props: {
    // [{ date: 'YYYY-MM-DD', value }]
    days: { type: Array, default: () => [] },
    // Plot-Höhe: 220 (Tablet hoch 200, Handy 150)
    height: { type: Number, default: 220 },
    // Abstand der Säulen: 4 (Tablet hoch 3, Handy 2)
    gap: { type: Number, default: 4 },
    // Breite der y-Achse: 52 (Handy 44)
    axisWidth: { type: Number, default: 52 },
    // x-Beschriftung an jedem n-ten Tag plus letzter Tag: 7 (Handy 14)
    labelEvery: { type: Number, default: 7 },
    // heutiges Datum 'YYYY-MM-DD': diese Säule ist orange, die letzte Beschriftung heißt dann „Heute“
    todayKey: { type: String, default: null },
    tooltips: { type: Boolean, default: true }
  },
  data() {
    return { hoverIndex: null, tapIndex: null };
  },
  computed: {
    touch() {
      return !!(this.$dichte && this.$dichte.touch);
    },
    max() {
      return this.days.reduce((acc, d) => Math.max(acc, Number(d.value) || 0), 0);
    },
    step() {
      return axisStep(this.max);
    },
    yMax() {
      return this.step * 3;
    },
    levels() {
      const values = [3, 2, 1, 0].map(f => f * this.step);
      const digits = decimalsOf(values);
      return values.map(value => ({
        value,
        text: formatNumber(value, digits) + NBSP + '€',
        top: (1 - value / this.yMax) * 100 + '%',
        lineTop: value === 0 ? 'calc(100% - 1px)' : (1 - value / this.yMax) * 100 + '%'
      }));
    },
    bars() {
      return this.days.map((d) => {
        const value = Number(d.value) || 0;
        const ratio = Math.max(0, Math.min(value / this.yMax, 1));
        return { date: d.date, value, ratio, today: d.date === this.todayKey, height: ratio * 100 + '%' };
      });
    },
    yAxisStyle() {
      return { width: this.axisWidth + 'px', height: this.height + 'px' };
    },
    plotStyle() {
      return { height: this.height + 'px' };
    },
    barsStyle() {
      return { gap: this.gap + 'px' };
    },
    activeIndex() {
      if (!this.tooltips) {
        return null;
      }
      return this.touch ? this.tapIndex : this.hoverIndex;
    },
    tip() {
      const i = this.activeIndex;
      const bar = i === null ? null : this.bars[i];
      if (!bar) {
        return null;
      }
      const n = this.bars.length;
      const text = moment(bar.date, 'YYYY-MM-DD').format('dd, DD.MM.') + ': ' + formatEuro(bar.value) +
        (bar.today ? ' (vorläufig)' : '');
      // über der Säule, oben nicht über die Fläche hinaus (Tooltip 26 hoch + 6 Abstand)
      const limit = Math.max(0, (this.height - 32) / this.height);
      const bottom = 'calc(' + Math.min(bar.ratio, limit) * 100 + '% + 6px)';
      let left;
      let transform;
      if (i < n * 0.15) {
        left = this.colEdge(i, n, 0);
        transform = 'none';
      } else if (i > n * 0.85) {
        left = this.colEdge(i, n, 1);
        transform = 'translateX(-100%)';
      } else {
        left = this.colEdge(i, n, 0.5);
        transform = 'translateX(-50%)';
      }
      return { text, style: { left, bottom, transform } };
    },
    xLabels() {
      const n = this.days.length;
      if (!n) {
        return [];
      }
      const last = n - 1;
      const step = Math.max(1, this.labelEvery);
      const indices = [];
      for (let i = 0; i <= last - Math.ceil(step / 2); i += step) {
        indices.push(i);
      }
      if (indices[indices.length - 1] !== last) {
        indices.push(last);
      }
      return indices.map((i) => {
        const day = this.days[i];
        let style;
        if (i === last) {
          style = { right: 0 };
        } else if (i === 0) {
          style = { left: 0 };
        } else {
          style = { left: this.colEdge(i, n, 0.5), transform: 'translateX(-50%)' };
        }
        const text = i === last && day.date === this.todayKey
          ? 'Heute'
          : moment(day.date, 'YYYY-MM-DD').format('DD.MM.');
        return { index: i, text, style };
      });
    },
    srText() {
      if (!this.days.length) {
        return '';
      }
      const first = this.days[0].date;
      const last = this.days[this.days.length - 1].date;
      const sum = this.days.reduce((acc, d) => acc + (Number(d.value) || 0), 0);
      let text = 'Einnahmen je Tag vom ' + formatDate(moment(first, 'YYYY-MM-DD')) + ' bis ' +
        formatDate(moment(last, 'YYYY-MM-DD')) + ': Summe ' + formatEuro(sum);
      const top = this.bars.reduce((best, bar) => (best === null || bar.value > best.value ? bar : best), null);
      if (top && top.value > 0) {
        text += ', höchster Tag ' + moment(top.date, 'YYYY-MM-DD').format('dd, DD.MM.') + ' mit ' + formatEuro(top.value);
      }
      return text + '.';
    }
  },
  watch: {
    days() {
      this.hoverIndex = null;
      this.tapIndex = null;
    },
    touch() {
      this.hoverIndex = null;
      this.tapIndex = null;
    }
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick);
  },
  methods: {
    // Position innerhalb der Säule i (0 = linke Kante, 0,5 = Mitte, 1 = rechte Kante) bei n Säulen mit Abstand gap
    colEdge(i, n, at) {
      const gaps = (n - 1) * this.gap;
      return 'calc((100% - ' + gaps + 'px) * ' + (i + at) / n + ' + ' + i * this.gap + 'px)';
    },
    onEnter(i) {
      if (this.tooltips && !this.touch) {
        this.hoverIndex = i;
      }
    },
    onTap(i) {
      if (this.tooltips && this.touch) {
        this.tapIndex = this.tapIndex === i ? null : i;
      }
    },
    onDocumentClick(event) {
      if (this.tapIndex !== null && this.$el && !this.$el.contains(event.target)) {
        this.tapIndex = null;
      }
    }
  }
};
</script>

<style scoped>
.ov-rev__chart {
  display: flex;
  gap: 8px;
}
.ov-rev__yaxis {
  flex: none;
  position: relative;
  font-size: 11px;
  line-height: 16px;
  color: var(--ink-3);
}
.ov-rev__ylabel {
  position: absolute;
  right: 0;
  transform: translateY(-50%);
  white-space: nowrap;
}
.ov-rev__main {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ov-rev__plot {
  position: relative;
}
.ov-rev__grid {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--line-2);
}
.ov-rev__grid--zero {
  background: var(--line);
}
.ov-rev__bars {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: flex-end;
}
.ov-rev__col {
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  display: flex;
  align-items: flex-end;
}
/* zwei Blautöne wie in den Kacheln: Säulen hell (--d-cmp), heute dunkel (--d-foc); unter der Maus mittleres Blau */
.ov-rev__bar {
  width: 100%;
  border-radius: 2px 2px 0 0;
  background: var(--d-cmp);
}
.ov-rev__bar--today {
  background: var(--d-foc);
}
.ov-rev__col--active .ov-rev__bar:not(.ov-rev__bar--today) {
  background: var(--d-std);
}
.ov-rev__tip {
  position: absolute;
  z-index: 1;
  box-sizing: border-box;
  padding: 4px 8px;
  border: 1px solid var(--line);
  border-radius: var(--r-ctl);
  background: var(--surface-raised);
  box-shadow: var(--sh-menu);
  font-size: 12px;
  line-height: 16px;
  color: var(--ink);
  white-space: nowrap;
  pointer-events: none;
}
.ov-rev__xaxis {
  position: relative;
  height: 16px;
  font-size: 11px;
  line-height: 16px;
  color: var(--ink-3);
}
.ov-rev__xlabel {
  position: absolute;
  top: 0;
  white-space: nowrap;
}
</style>
